import { useEffect, useMemo, useState } from 'react'
import { ChevronRight, Zap } from 'lucide-react'
import { Shell, useShellMeta } from '../../../admin/Shell.js'
import { Select, usePermissions } from '../../../admin/primitives.js'
import { SegmentedControl } from '../../../admin/pickers.js'
import { Link } from '../../../router.js'
import { fmtDateHeure } from '../../api.js'
import {
  BandeauFige, BandeauLecture, LIBELLE_ETAT, Pastille, PastilleEtat, TON_ETAT, TON_GRAVITE, fmtAge, renommerSalles, useNomsSalles, useVueSonde, type Ton,
} from '../commun.js'
import { estFige } from '../hub/modele.js'
import {
  Fait, IncidentsBrefs, LigneAppareil, Quand, Retour, Section, Squelette, Vide, donneeDe,
} from './composants.js'
import { Courbe } from './Courbe.js'
import { FormulaireAnnotation } from './Formulaires.js'
import {
  CHEMIN_A_VALIDER, cheminRegistreAppareil, LIBELLE_CRITICITE, LIBELLE_LIEN, LIBELLE_RELATION, LIBELLE_SOUS_CLE, SOUS_CLE, UID_APPAREIL,
  cheminAppareil, cheminIncident, cheminSalle, estDocAnnotations, estDocAppareil, estDocChronologie, estDocSeries, estDocVoisins,
  fmtValeur, groupesParTable, libelleType, sousCleParDefaut, sousCles,
  type AlimentationAppareil, type DocAppareil, type EntreeChronologie, type MesureDisponible, type Voisin,
} from './modele.js'
import { CHEMIN_ONDULEURS, cheminOnduleur, fmtMinutes, texteAlimentations } from '../batiment/onduleurs-modele.js'
import { useMenuSonde } from '../menu-sonde.js'
import { useMenuSalle } from '../menu-salle.js'
import { BoutonActualiser } from '../actualiser.js'

/**
 * FICHE APPAREIL — /infrastructure/appareils/:uid (21/10/2026).
 *
 * Lit `devices/:uid`, `/timeline`, `/neighbors`, `/series` et les
 * annotations de Sonde. Conséquence d'abord : l'état et depuis quand, puis
 * « Pourquoi cet état ? » (témoins, cause, fraîcheur), la chaîne d'amont
 * cliquable, les courbes de la famille (un trou est un trou), la
 * chronologie, les voisins. Les identifiants techniques (MAC, IP,
 * empreintes) n'apparaissent qu'avec `sonde.details.read`.
 */

const LIBELLE_ROLE_TEMOIN: Record<string, string> = { authority: 'Témoin principal', confirmation: 'Témoin de confirmation' }
const TON_SOURCE: Record<string, Ton> = { OK: 'ok', DEGRADED: 'warn', UNAVAILABLE: 'gris', DISABLED: 'gris', NOT_CONFIGURED: 'gris' }
const LIBELLE_SOURCE: Record<string, string> = { OK: 'Source lue', DEGRADED: 'Source dégradée', UNAVAILABLE: 'Source indisponible', DISABLED: 'Source désactivée', NOT_CONFIGURED: 'Source non configurée' }
const LIBELLE_OBSERVATION: Record<string, string> = { FRESH: 'donnée fraîche', STALE: 'donnée périmée', SOURCE_UNAVAILABLE: 'source indisponible, dernier état gardé' }
const LIBELLE_CYCLE: Record<string, string> = { active: 'En service', spare: 'De réserve', retired: 'Retiré', discovered: 'Découvert', ignored: 'Ignoré' }
const LIBELLE_SUIVI: Record<string, string> = { presence: 'Présence seulement', detailed: 'Suivi détaillé', ignored: 'Non suivi' }
const LIBELLE_PROPOSITION: Record<string, string> = {
  parc_signalement: 'Signalement Parc proposé', retire_ghost: 'Fiche fantôme', replaced_unit: 'Exemplaire remplacé', power_domain: 'Domaine d’alimentation appris',
}
const LIBELLE_STATUT_PROPOSITION: Record<string, string> = { proposed: 'À décider', accepted: 'Acceptée', dismissed: 'Écartée', expired: 'Expirée' }
const LIBELLE_ORIGINE_PARC: Record<string, string> = { suggested: 'Proposée par Sonde', confirmed: 'Confirmée', rejected: 'Refusée' }
const SCHEMAS_MATERIEL = new Set(['mac', 'unifi_device_mac', 'unifi_client_mac', 'serial'])

/* ─────────────── En-tête ─────────────── */

function EnTete({ d, fige, nomZone }: { d: DocAppareil; fige: boolean; nomZone: string | null }) {
  const a = d.device
  const s = a.state
  const ton = fige ? 'gris' : TON_ETAT[s.display_state] ?? 'gris'
  const amont = d.upstream_chain[0]
  const menu = useMenuSonde()
  const menuSalle = useMenuSalle()
  return (
    <header className={`sv-fiche-hero sv-fiche-hero--${ton}`} {...menu.appareil({ uid: a.uid, nom: a.display_name, zone_code: a.zone_code })}>
      <div className="sv-fiche-hero__haut">
        <div className="sv-fiche-hero__id">
          <p className="sv-fiche-hero__sur">
            {a.family_label} · {libelleType(a.kind)}
            {a.zone_code ? <> · <Link href={cheminSalle(a.zone_code)} {...menuSalle.salle({ code: a.zone_code, label: nomZone }, { imbrique: true })}>{nomZone ?? a.zone_code}</Link></> : null}
            {a.zone_detail ? ` · ${a.zone_detail}` : ''}
          </p>
          <h1 className="lc-title sv-fiche-hero__titre">{a.display_name}</h1>
          <div className="sv-fiche-hero__pastilles">
            <PastilleEtat etat={s.display_state} libelle={s.display_label} />
            {s.flapping ? <Pastille ton="warn">Instable</Pastille> : null}
            {s.exception ? <Pastille ton="maint">{s.exception.kind === 'unplugged' ? 'Débranché volontairement' : s.exception.kind === 'ignore' ? 'Ignoré' : 'Exception posée'}</Pastille> : null}
            <span className="sv-fiche-crit">{LIBELLE_CRITICITE[a.criticality] ?? a.criticality}</span>
          </div>
        </div>
        <div className="sv-fiche-hero__gestes">
          <BoutonActualiser cible={{ kind: 'device', uid: a.uid, nom: a.display_name }} />
        </div>
      </div>
      <p className="sv-conseq sv-fiche-hero__phrase">{renommerSalles(d.why_fr)}</p>
      <dl className="sv-fiche-faits">
        <Fait libelle="Depuis" sous={s.since ? fmtDateHeure(s.since) : null}>{s.since ? <Quand iso={s.since} /> : '—'}</Fait>
        <Fait libelle="Dernier relevé" sous={s.observation ? LIBELLE_OBSERVATION[s.observation] ?? s.observation : null}>
          {s.observed_at ? <><Quand iso={s.observed_at} /> · {fmtAge(s.data_age_s)}</> : '—'}
        </Fait>
        <Fait libelle="Confiance" sous="selon les témoins">{s.confidence === null ? '—' : `${s.confidence} %`}</Fait>
        <Fait libelle="Branché sur" sous={amont ? `${LIBELLE_LIEN[amont.link_kind] ?? amont.link_kind}${amont.port !== null ? ` · port ${amont.port}` : ''}` : null}>
          {amont ? <Link href={cheminAppareil(amont.uid)} {...menu.appareil({ uid: amont.uid, nom: amont.display_name })}>{amont.display_name}</Link> : '—'}
        </Fait>
      </dl>
    </header>
  )
}

/* ─────────────── Pourquoi cet état ? ─────────────── */

/**
 * Un témoin qui ne voit pas l'appareil n'est rouge que si l'appareil DEVRAIT
 * être vu et que la source répond : éteint comme prévu ou en maintenance =
 * neutre ; source muette = gris.
 */
function tonTemoin(e: DocAppareil['evidence'][number], etat: string): Ton {
  if (e.present === null) return 'gris'
  if (e.present) return 'ok'
  if (etat === 'OFF_EXPECTED') return 'off'
  if (etat === 'MAINTENANCE') return 'maint'
  if (e.source_status && e.source_status !== 'OK') return 'gris'
  return etat === 'OFFLINE' ? 'err' : 'warn'
}

function Pourquoi({ d, details }: { d: DocAppareil; details: boolean }) {
  const s = d.device.state
  return (
    <Section titre="Pourquoi cet état ?" id="sv-app-pourquoi" indice={`${d.evidence.length} témoin${d.evidence.length > 1 ? 's' : ''}`}>
      <p className="sv-fiche-texte">{renommerSalles(d.why_fr)}</p>
      {s.cause && s.cause !== 'SELF' ? (
        <p className="sv-fiche-texte">
          Cause : <strong>{s.cause_label ?? s.cause}</strong>
          {s.cause_uid ? <> — <Link href={cheminAppareil(s.cause_uid)}>{s.cause_uid}</Link></> : s.cause_ref ? <> — <span className="sv-mono">{s.cause_ref}</span></> : null}
        </p>
      ) : null}
      {d.evidence.length === 0 ? (
        <Vide><span>Aucun témoin n’a encore parlé de cet appareil : Sonde ne le déclare ni en ligne ni en panne.</span></Vide>
      ) : (
        <ul className="sv-fiche-temoins">
          {d.evidence.map((e, i) => (
            <li key={`${e.witness}-${i}`} className="sv-fiche-temoin">
              <div className="sv-fiche-temoin__tete">
                <span className="sv-fiche-temoin__nom">{LIBELLE_ROLE_TEMOIN[e.role] ?? e.role}</span>
                <Pastille ton={tonTemoin(e, s.display_state)}>{e.present === null ? 'Présence inconnue' : e.present ? 'Le voit présent' : 'Ne le voit pas'}</Pastille>
              </div>
              <p className="sv-fiche-temoin__meta">
                {e.source_label ?? e.source_code ?? 'Source inconnue'}
                {e.source_status ? <> · <span className={`sv-fiche-ton-texte--${TON_SOURCE[e.source_status] ?? 'gris'}`}>{LIBELLE_SOURCE[e.source_status] ?? e.source_status}</span></> : null}
                {' · '}{e.observed_at ? <>vu à <Quand iso={e.observed_at} /> ({fmtAge(e.data_age_s)})</> : 'jamais vu'}
              </p>
              {details ? <p className="sv-mono">{e.witness}</p> : null}
              {details && e.detail && Object.keys(e.detail).length ? (
                <dl className="sv-fiche-kv">
                  {Object.entries(e.detail).slice(0, 12).map(([k, v]) => (
                    <div key={k}><dt>{k}</dt><dd className="sv-mono">{typeof v === 'object' ? JSON.stringify(v) : String(v)}</dd></div>
                  ))}
                </dl>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </Section>
  )
}

/* ─────────────── Chaîne d'amont ─────────────── */

function ChaineAmont({ d }: { d: DocAppareil }) {
  const chaine = d.upstream_chain.slice().reverse()
  const menu = useMenuSonde()
  return (
    <Section titre="Chaîne d’amont" id="sv-app-amont" indice={d.downstream_count ? `${d.downstream_count} appareil${d.downstream_count > 1 ? 's' : ''} en dépend${d.downstream_count > 1 ? 'ent' : ''}` : undefined}>
      {chaine.length === 0 ? (
        <Vide><span>Aucun amont connu au registre : Sonde ne sait pas où cet appareil est branché.</span></Vide>
      ) : (
        <ol className="sv-fiche-chaine">
          {chaine.map((m) => (
            <li key={m.uid} className="sv-fiche-chaine__maillon">
              <Link className={`sv-fiche-chaine__noeud sv-fiche-chaine__noeud--${TON_ETAT[m.display_state] ?? 'gris'}`} href={cheminAppareil(m.uid)}
                    title={LIBELLE_ETAT[m.display_state] ?? m.display_state} {...menu.appareil({ uid: m.uid, nom: m.display_name })}>
                <span className="sv-ton__point" aria-hidden="true" />
                <span>{m.display_name}</span>
                {m.port !== null ? <span className="sv-mono">p{m.port}</span> : null}
              </Link>
              <span className="sv-fiche-chaine__lien">{LIBELLE_LIEN[m.link_kind] ?? m.link_kind}</span>
            </li>
          ))}
          <li className="sv-fiche-chaine__maillon">
            <span className={`sv-fiche-chaine__noeud sv-fiche-chaine__noeud--ici sv-fiche-chaine__noeud--${TON_ETAT[d.device.state.display_state] ?? 'gris'}`}>
              <span className="sv-ton__point" aria-hidden="true" /><span>{d.device.display_name}</span>
            </span>
          </li>
        </ol>
      )}
    </Section>
  )
}

/* ─────────────── Courbes ─────────────── */

type Periode = '1h' | '6h' | '24h' | '7j'
const DUREE: Record<Periode, number> = { '1h': 3_600_000, '6h': 6 * 3_600_000, '24h': 86_400_000, '7j': 7 * 86_400_000 }

/** L'instant de référence, arrondi à la minute : le chemin de lecture ne change qu'une fois par minute. */
function useMinute(): number {
  const [m, setM] = useState(() => Math.floor(Date.now() / 60_000) * 60_000)
  useEffect(() => {
    const t = setInterval(() => setM(Math.floor(Date.now() / 60_000) * 60_000), 60_000)
    return () => clearInterval(t)
  }, [])
  return m
}

function libelleCle(table: string, v: string | number): string {
  return `${LIBELLE_SOUS_CLE[table] ?? 'Clé'} ${v}`
}

function GroupeCourbes({ uid, table, mesures, debutMs, latest, dispo }: {
  uid: string; table: string; mesures: string[]; debutMs: number; latest: DocAppareil['latest']; dispo: MesureDisponible[]
}) {
  const nomSous = SOUS_CLE[table]
  const cles = useMemo(() => sousCles(latest, table), [latest, table])
  const toutesPossibles = cles.length > 0 && cles.length * mesures.length <= 48
  const [cle, setCle] = useState<string | null>(() => {
    if (!nomSous || cles.length === 0) return null
    return cles.length <= 12 && toutesPossibles ? '*' : sousCleParDefaut(latest, table, mesures[0])
  })
  const q = new URLSearchParams({ metrics: mesures.join(','), from: new Date(debutMs).toISOString(), step: 'auto' })
  if (cle && cle !== '*') q.set('key', cle)
  const { lecture, precedente } = useVueSonde(`devices/${encodeURIComponent(uid)}/series?${q.toString()}`, estDocSeries, 120_000)
  const { data, perime } = donneeDe(lecture, precedente)
  const debut = data ? Date.parse(data.from) : debutMs
  const fin = data ? Date.parse(data.to) : Date.now()
  const parCode = new Map(dispo.map((x) => [x.metric, x]))

  return (
    <div className={`sv-fiche-groupe${perime ? ' sv-fiche-perime' : ''}`}>
      {nomSous && cles.length > 1 ? (
        <div className="sv-fiche-groupe__cle">
          <Select compact ariaLabel={LIBELLE_SOUS_CLE[table] ?? 'Clé'} value={cle ?? ''} onChange={setCle}
            options={[...(toutesPossibles ? [{ value: '*', label: `Tous (${cles.length})` }] : []), ...cles.map((c) => ({ value: c, label: libelleCle(table, c) }))]} />
        </div>
      ) : null}
      {lecture && lecture.etat !== 'ok' && !data ? <p className="sv-fiche-doux">{lecture.message}</p> : null}
      {!data && !lecture ? <Squelette lignes={2} /> : null}
      {data ? mesures.map((m) => {
        const series = data.series.filter((s) => s.metric === m)
        const def = parCode.get(m)
        const titre = def?.label ?? series[0]?.label ?? m
        const unite = def?.unit ?? series[0]?.unit ?? null
        const agg = def?.agg ?? series[0]?.agg ?? 'gauge'
        return (
          <figure key={m} className="sv-fiche-courbe">
            <figcaption className="sv-fiche-courbe__titre">
              <span>{titre}</span>
              {series.length === 1 ? <span className="sv-fiche-courbe__valeur">{fmtValeur(derniereValeur(series[0]!.points), unite)}</span> : null}
            </figcaption>
            {series.length === 0 ? (
              <p className="sv-fiche-doux">Aucune donnée sur la période — pas un zéro : Sonde n’a rien relevé.</p>
            ) : series.length === 1 ? (
              <Courbe points={series[0]!.points} seuilS={series[0]!.gap_threshold_s} unite={unite} agg={agg} debut={debut} fin={fin} libelle={titre} />
            ) : (
              <div className="sv-fiche-multiples">
                {series.map((s) => {
                  const k = s.key && nomSous ? s.key[nomSous] : null
                  const nom = k !== null && k !== undefined ? libelleCle(table, k) : s.key ? Object.values(s.key).join(' · ') : titre
                  return (
                    <div key={`${m}-${nom}`} className="sv-fiche-multiple">
                      <span className="sv-fiche-multiple__nom">{nom}<span>{fmtValeur(derniereValeur(s.points), unite)}</span></span>
                      <Courbe points={s.points} seuilS={s.gap_threshold_s} unite={unite} agg={agg} debut={debut} fin={fin} hauteur={64} libelle={`${titre} — ${nom}`} />
                    </div>
                  )
                })}
              </div>
            )}
          </figure>
        )
      }) : null}
    </div>
  )
}

function derniereValeur(points: [number, number | null, number | null, number | null][]): number | null {
  for (let i = points.length - 1; i >= 0; i--) { const v = points[i]![1]; if (v !== null) return v }
  return null
}

function Courbes({ d }: { d: DocAppareil }) {
  const dispo = d.series_available
  const conseillees = d.suggested_metrics.length ? d.suggested_metrics : dispo.filter((x) => x.agg !== 'state').slice(0, 4).map((x) => x.metric)
  const [choisies, setChoisies] = useState<string[]>(conseillees)
  const [periode, setPeriode] = useState<Periode>('6h')
  const [toutes, setToutes] = useState(false)
  const minute = useMinute()
  const debutMs = minute - DUREE[periode]
  const groupes = groupesParTable(choisies, dispo)
  const basculer = (m: string) => setChoisies((c) => c.includes(m) ? c.filter((x) => x !== m) : c.length >= 16 ? c : [...c, m])
  const proposees = toutes ? dispo : dispo.filter((x) => conseillees.includes(x.metric) || choisies.includes(x.metric))

  return (
    <Section titre="Courbes" id="sv-app-courbes" indice="cyan = relevé de Sonde · hachures = pas de donnée"
             actions={dispo.length ? (
               <SegmentedControl size="sm" ariaLabel="Période" value={periode} onChange={setPeriode}
                 options={[{ value: '1h', label: '1 h' }, { value: '6h', label: '6 h' }, { value: '24h', label: '24 h' }, { value: '7j', label: '7 j' }]} />
             ) : undefined}>
      {dispo.length === 0 ? (
        <Vide><span>Sonde n’a aucune mesure enregistrée pour cet appareil sur les dernières 24 h : il est suivi par sa présence seulement, ou sa source ne publie pas de mesures.</span></Vide>
      ) : (
        <>
          <div className="sv-fiche-puces" role="group" aria-label="Mesures affichées">
            {proposees.map((x) => (
              <button key={x.metric} type="button" className={`sv-fiche-puce${choisies.includes(x.metric) ? ' sv-fiche-puce--on' : ''}`}
                      aria-pressed={choisies.includes(x.metric)} onClick={() => basculer(x.metric)}>
                {x.label}{x.unit ? <span className="sv-fiche-puce__u"> {x.unit}</span> : null}
              </button>
            ))}
            {dispo.length > proposees.length || toutes ? (
              <button type="button" className="lc-btn lc-btn--ghost sv-fiche-plus" onClick={() => setToutes((t) => !t)}>
                {toutes ? 'Mesures conseillées seulement' : `Toutes les mesures (${dispo.length})`}
              </button>
            ) : null}
          </div>
          {groupes.length === 0 ? <p className="sv-fiche-doux">Choisissez au moins une mesure.</p> : groupes.map((g) => (
            <GroupeCourbes key={`${g.table}-${periode}`} uid={d.device.uid} table={g.table} mesures={g.mesures} debutMs={debutMs} latest={d.latest} dispo={dispo} />
          ))}
        </>
      )}
    </Section>
  )
}

/* ─────────────── Chronologie ─────────────── */

function tonEntree(e: EntreeChronologie): Ton {
  if (e.type === 'state') {
    const obs = e.data['observation']
    if (obs && obs !== 'FRESH') return 'gris'
    const to = e.data['to_status']
    return typeof to === 'string' ? TON_ETAT[to] ?? 'gris' : 'gris'
  }
  if (e.type === 'exception') return 'maint'
  return TON_GRAVITE[e.severity] ?? 'gris'
}

function messageEntree(e: EntreeChronologie): string | null {
  const p = e.data['payload']
  if (p && typeof p === 'object' && !Array.isArray(p)) {
    const m = (p as Record<string, unknown>)['message_fr']
    if (typeof m === 'string' && m) return m
  }
  return null
}

function Chronologie({ uid }: { uid: string }) {
  const [jours, setJours] = useState<'1' | '7'>('1')
  const [depuis, setDepuis] = useState<string | null>(null)
  const [tout, setTout] = useState(false)
  const chemin = `devices/${encodeURIComponent(uid)}/timeline${depuis ? `?from=${encodeURIComponent(depuis)}` : ''}`
  const { lecture, precedente } = useVueSonde(chemin, estDocChronologie, 60_000)
  const { data } = donneeDe(lecture, precedente)
  const entrees = data?.entries ?? []
  const visibles = tout ? entrees : entrees.slice(0, 25)
  return (
    <Section titre="Chronologie" id="sv-app-chrono" indice={data ? `${entrees.length} entrée${entrees.length > 1 ? 's' : ''}${data.truncated ? ' (tronquée)' : ''}` : undefined}
             actions={<SegmentedControl size="sm" ariaLabel="Période de la chronologie" value={jours}
               onChange={(v) => { setJours(v); setDepuis(v === '7' ? new Date(Date.now() - 7 * 86_400_000).toISOString() : null) }}
               options={[{ value: '1', label: '24 h' }, { value: '7', label: '7 jours' }]} />}>
      {lecture && lecture.etat !== 'ok' && !data ? <p className="sv-fiche-doux">{lecture.message}</p> : null}
      {!data && !lecture ? <Squelette lignes={4} /> : null}
      {data && entrees.length === 0 ? <Vide><span>Rien à signaler sur la période : aucun changement d’état, événement ni incident.</span></Vide> : null}
      {visibles.length ? (
        <ol className="sv-fiche-chrono">
          {visibles.map((e, i) => {
            const msg = messageEntree(e)
            return (
              <li key={`${e.at}-${i}`} className={`sv-fiche-chrono__e sv-fiche-chrono__e--${tonEntree(e)}`}>
                <span className="sv-fiche-chrono__heure sv-mono"><Quand iso={e.at} /></span>
                <span className="sv-fiche-chrono__corps">
                  <span className="sv-fiche-chrono__titre">
                    {e.type === 'incident' && e.ref ? <Link href={cheminIncident(e.ref)}>{e.title_fr}</Link> : e.title_fr}
                  </span>
                  {msg ? <span className="sv-fiche-chrono__detail">{msg}</span> : null}
                  {e.detail_fr ? <span className="sv-fiche-chrono__detail">{e.detail_fr}</span> : null}
                </span>
              </li>
            )
          })}
        </ol>
      ) : null}
      {entrees.length > 25 ? (
        <button type="button" className="lc-btn lc-btn--ghost sv-fiche-plus" onClick={() => setTout((t) => !t)}>
          {tout ? 'Replier' : `Voir les ${entrees.length - 25} entrées plus anciennes`}
        </button>
      ) : null}
    </Section>
  )
}

/* ─────────────── Voisins ─────────────── */

function Voisins({ uid }: { uid: string }) {
  const { lecture, precedente } = useVueSonde(`devices/${encodeURIComponent(uid)}/neighbors`, estDocVoisins, 60_000)
  const { data } = donneeDe(lecture, precedente)
  const groupes = useMemo(() => {
    const g = new Map<Voisin['relation'], Voisin[]>()
    for (const v of data?.neighbors ?? []) g.set(v.relation, [...(g.get(v.relation) ?? []), v])
    return (['upstream', 'downstream', 'sibling', 'same_zone'] as const).flatMap((r) => g.get(r)?.length ? [[r, g.get(r)!] as const] : [])
  }, [data])
  return (
    <Section titre="Voisins" id="sv-app-voisins" indice={data ? `${data.neighbors.length}` : undefined}>
      {lecture && lecture.etat !== 'ok' && !data ? <p className="sv-fiche-doux">{lecture.message}</p> : null}
      {!data && !lecture ? <Squelette lignes={2} /> : null}
      {data && groupes.length === 0 ? <Vide><span>Aucun voisin connu : pas de lien au registre ni d’appareil du même type dans la salle.</span></Vide> : null}
      {groupes.map(([rel, liste]) => (
        <div key={rel} className="sv-fiche-voisins">
          <h3 className="sv-fiche-sous-titre">{LIBELLE_RELATION[rel]}</h3>
          <ul className="sv-fiche-liste">
            {liste.map((v) => (
              <LigneAppareil key={v.device.uid} a={v.device}
                extra={v.link_kind || v.port !== null ? `${v.link_kind ? LIBELLE_LIEN[v.link_kind] ?? v.link_kind : ''}${v.port !== null ? ` port ${v.port}` : ''}`.trim() : undefined} />
            ))}
          </ul>
        </div>
      ))}
    </Section>
  )
}

/* ─────────────── Matériel, remplacements, Parc ─────────────── */

/** « Alimentation » : son onduleur et combien de temps il tient si le courant coupe (page Onduleurs, 27/10/2026). */
function LigneAlimentation({ p }: { p: AlimentationAppareil }) {
  const duree = p.limited_by === 'mains_only' ? 'aucune autonomie' : p.runtime_min !== null ? `${fmtMinutes(p.runtime_min)} d’autonomie` : null
  return (
    <div>
      <dt>Alimentation</dt>
      <dd>
        {p.text_fr || [p.ups_label, duree].filter(Boolean).join(' · ') || '—'}
        <span className="sv-fiche-fait__sous">
          {p.feeds.length ? `${texteAlimentations(p.feeds)} · ` : ''}
          <Link href={p.ups_uid ? cheminOnduleur(p.ups_uid) : CHEMIN_ONDULEURS}>voir les onduleurs</Link>
        </span>
      </dd>
    </div>
  )
}

function Materiel({ d, details, peutRegistre }: { d: DocAppareil; details: boolean; peutRegistre: boolean }) {
  const a = d.device
  const detailAutorite = d.evidence.find((e) => e.role === 'authority' && e.detail)?.detail ?? d.evidence.find((e) => e.detail)?.detail ?? null
  const modele = detailAutorite && typeof detailAutorite['model'] === 'string' ? detailAutorite['model'] : null
  const version = detailAutorite && typeof detailAutorite['version'] === 'string' ? detailAutorite['version'] : null
  const anciens = d.identifiers.filter((i) => i.valid_to !== null && SCHEMAS_MATERIEL.has(i.scheme))
  const remplacements = d.proposals.filter((p) => p.kind === 'replaced_unit')
  const autres = d.proposals.filter((p) => p.kind !== 'replaced_unit')
  return (
    <Section titre="Matériel et remplacements" id="sv-app-materiel">
      <dl className="sv-fiche-dl">
        <div><dt>Type</dt><dd>{libelleType(a.kind)}</dd></div>
        <div><dt>Modèle</dt><dd>{modele ?? '—'}{version ? <span className="sv-fiche-fait__sous">version {version}</span> : null}</dd></div>
        <div><dt>Cycle de vie</dt><dd>{LIBELLE_CYCLE[a.lifecycle] ?? a.lifecycle} · {LIBELLE_SUIVI[a.tracking] ?? a.tracking}</dd></div>
        <div><dt>Santé globale</dt><dd>{a.affects_global_health ? 'Compte dans l’état du club' : 'Ne compte pas dans l’état du club'}</dd></div>
        {a.power_domain ? <div><dt>Domaine d’alimentation</dt><dd>{renommerSalles(a.power_domain.label)}</dd></div> : null}
        {d.power ? <LigneAlimentation p={d.power} /> : null}
        <div><dt>Au registre depuis</dt><dd>{fmtDateHeure(a.created_at)}{peutRegistre ? <> · <Link href={cheminRegistreAppareil(a.uid)}>modifier au registre</Link></> : null}</dd></div>
        {a.tags.length ? <div><dt>Étiquettes</dt><dd>{a.tags.join(', ')}</dd></div> : null}
        {a.notes ? <div><dt>Notes</dt><dd>{a.notes}</dd></div> : null}
      </dl>
      <h3 className="sv-fiche-sous-titre">Remplacements</h3>
      {remplacements.length === 0 && anciens.length === 0 ? <p className="sv-fiche-doux">Aucun exemplaire remplacé connu.</p> : null}
      {remplacements.map((p) => (
        <p key={p.id} className="sv-fiche-texte">
          <Pastille ton={p.status === 'proposed' ? 'warn' : 'gris'}>{LIBELLE_STATUT_PROPOSITION[p.status] ?? p.status}</Pastille> {p.reason} <span className="sv-fiche-doux">(<Quand iso={p.created_at} />)</span>
        </p>
      ))}
      {anciens.length ? (
        details ? (
          <ul className="sv-fiche-kvlist">
            {anciens.map((i) => <li key={`${i.scheme}-${i.value}-${i.valid_from}`}><span className="sv-mono">{i.value}</span> · {i.scheme} · jusqu’au {fmtDateHeure(i.valid_to)}</li>)}
          </ul>
        ) : <p className="sv-fiche-doux">{anciens.length} exemplaire{anciens.length > 1 ? 's' : ''} précédent{anciens.length > 1 ? 's' : ''} (identifiants visibles avec le droit « détails techniques »).</p>
      ) : null}
      {autres.length ? (
        <>
          <h3 className="sv-fiche-sous-titre">Propositions de Sonde</h3>
          <ul className="sv-fiche-kvlist">
            {autres.map((p) => (
              <li key={p.id}>
                <strong>{LIBELLE_PROPOSITION[p.kind] ?? p.kind}</strong> · {LIBELLE_STATUT_PROPOSITION[p.status] ?? p.status} — {p.reason}
              </li>
            ))}
          </ul>
          {peutRegistre && autres.some((p) => p.status === 'proposed') ? <Link className="lc-btn lc-btn--ghost sv-fiche-plus" href={CHEMIN_A_VALIDER}>Décider dans « À valider »</Link> : null}
        </>
      ) : null}
    </Section>
  )
}

function FicheParc({ d }: { d: DocAppareil }) {
  return (
    <Section titre="Fiche Parc" id="sv-app-parc">
      {d.parc.length === 0 ? <p className="sv-fiche-doux">Aucune fiche Parc rattachée à cet appareil.</p> : (
        <ul className="sv-fiche-kvlist">
          {d.parc.map((p) => {
            const resume = p.snapshot ? Object.entries(p.snapshot).filter(([, v]) => typeof v === 'string' || typeof v === 'number').slice(0, 4) : []
            return (
              <li key={p.parc_item_id}>
                <strong>Fiche n° {p.parc_item_id}</strong> · {LIBELLE_ORIGINE_PARC[p.origin] ?? p.origin}
                {p.confirmed_by ? ` par ${p.confirmed_by}` : ''}{p.confirmed_at ? <> le {fmtDateHeure(p.confirmed_at)}</> : null}
                {p.confidence !== null && p.origin === 'suggested' ? ` · confiance ${p.confidence} %` : ''}
                {resume.length ? <span className="sv-fiche-fait__sous">{resume.map(([, v]) => String(v)).join(' · ')}</span> : null}
              </li>
            )
          })}
        </ul>
      )}
    </Section>
  )
}

function DetailsTechniques({ d }: { d: DocAppareil }) {
  return (
    <Section titre="Détails techniques" id="sv-app-tech" indice="droit « détails techniques »">
      <dl className="sv-fiche-dl">
        <div><dt>Identifiant Sonde</dt><dd className="sv-mono">{d.device.uid}</dd></div>
        {d.device.profile_code ? <div><dt>Profil</dt><dd className="sv-mono">{d.device.profile_code}</dd></div> : null}
        <div><dt>Vu la première fois</dt><dd>{fmtDateHeure(d.device.first_seen_at)}</dd></div>
        <div><dt>Vu la dernière fois</dt><dd>{fmtDateHeure(d.device.last_seen_at)}</dd></div>
      </dl>
      {d.identifiers.length === 0 ? <p className="sv-fiche-doux">Aucun identifiant enregistré.</p> : (
        <div className="lc-scroll sv-fiche-table-boite">
          <table className="lc-table sv-fiche-table">
            <thead><tr><th>Schéma</th><th>Valeur</th><th>VLAN</th><th>Source</th><th>Valide</th></tr></thead>
            <tbody>
              {d.identifiers.map((i) => (
                <tr key={`${i.scheme}-${i.value}-${i.valid_from}`} className={i.valid_to ? 'sv-fiche-table__ancien' : undefined}>
                  <td>{i.scheme}</td>
                  <td className="sv-mono">{i.value}</td>
                  <td>{i.vlan ?? '—'}</td>
                  <td>{i.source ?? '—'}</td>
                  <td>{i.valid_to ? `jusqu’au ${fmtDateHeure(i.valid_to)}` : 'actuel'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  )
}

/* ─────────────── Annotations ─────────────── */

function Annotations({ uid, zone, peutAnnoter }: { uid: string; zone: string | null; peutAnnoter: boolean }) {
  const { lecture, precedente, recharger } = useVueSonde(`annotations?device=${encodeURIComponent(uid)}&limit=20`, estDocAnnotations, 60_000)
  const { data } = donneeDe(lecture, precedente)
  const items = data?.items ?? []
  return (
    <Section titre="Annotations de la régie" id="sv-app-annot" indice={data ? `${items.length}` : undefined}>
      {lecture && lecture.etat !== 'ok' && !data ? <p className="sv-fiche-doux">{lecture.message}</p> : null}
      {data && items.length === 0 ? <p className="sv-fiche-doux">Aucune annotation sur cet appareil.</p> : null}
      {items.length ? (
        <ul className="sv-fiche-notes">
          {items.map((n) => (
            <li key={n.id}>
              <span className="sv-fiche-notes__tete"><strong>{n.kind_label}</strong> · {n.author.name} · <Quand iso={n.at} /></span>
              <span>{n.body}</span>
            </li>
          ))}
        </ul>
      ) : null}
      {peutAnnoter ? <FormulaireAnnotation cible={zone ? { device_uid: uid, zone_code: zone } : { device_uid: uid }} onFait={recharger} /> : null}
    </Section>
  )
}

/* ─────────────── Page ─────────────── */

export function FicheAppareilPage({ uid }: { uid: string }) {
  const meta = useShellMeta()
  const { permissions } = usePermissions()
  const u = uid.toLowerCase()
  const valide = UID_APPAREIL.test(u)
  const { lecture, precedente } = useVueSonde(valide ? `devices/${encodeURIComponent(u)}` : null, estDocAppareil, 15_000)
  const { data: d, perime } = donneeDe(lecture, precedente)
  const details = permissions.includes('sonde.details.read')
  const repli = d?.device.zone_code ? cheminSalle(d.device.zone_code) : '/infrastructure'
  const nomZone = useNomsSalles()(d?.device.zone_code)
  // Collecteur muet ou lecture en échec : la dernière donnée reste, en gris.
  const fige = perime || estFige(d?.collector)

  return (
    <Shell active="infrastructure" meta={meta}>
      <div className="lc-page lc-page--wide lc-stagger sv-fiche">
        <Retour repli={repli} libelle={nomZone ?? 'Supervision'} />
        {!valide ? <Vide><strong>Adresse d’appareil invalide.</strong><span>Un identifiant d’appareil s’écrit en minuscules, par exemple ap-005-salon.</span></Vide> : null}
        <BandeauLecture lecture={lecture} depuis={precedente?.generated_at ?? null} />
        {d ? <BandeauFige collector={d.collector} /> : null}
        {valide && !d && !lecture ? <Squelette lignes={6} /> : null}
        {d ? (
          <div className={fige ? 'sv-fiche-perime' : undefined} key={d.device.uid}>
            <EnTete d={d} fige={fige} nomZone={nomZone} />
            <div className="sv-fiche-grille sv-fiche-grille--principal-dabord">
              <div className="sv-fiche-grille__cote">
                <ChaineAmont d={d} />
                <Section titre="Incidents" id="sv-app-inc" indice="30 derniers jours">
                  <IncidentsBrefs items={d.incidents} vide="Aucun incident sur cet appareil depuis 30 jours." />
                </Section>
                <Annotations uid={d.device.uid} zone={d.device.zone_code} peutAnnoter={permissions.includes('sonde.annotate')} />
                <Materiel d={d} details={details} peutRegistre={permissions.includes('sonde.registry.manage')} />
                <FicheParc d={d} />
                {details ? <DetailsTechniques d={d} /> : null}
              </div>
              <div className="sv-fiche-grille__principal">
                <Pourquoi d={d} details={details} />
                <Courbes d={d} />
                <Chronologie uid={d.device.uid} />
                <Voisins uid={d.device.uid} />
              </div>
            </div>
          </div>
        ) : null}
        <p className="sv-fiche-pied"><Zap size={14} aria-hidden="true" /> Source : Sonde{d ? <> · lu à <Quand iso={d.generated_at} /></> : null}
          <ChevronRight size={14} aria-hidden="true" /> <Link href="/infrastructure/incidents">Incidents</Link></p>
      </div>
    </Shell>
  )
}
